import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { BLOG_POSTS } from "../data/blogPosts";
import CTABand from "../components/CTABand";
import { useSEO } from "../lib/seo";

export default function Blog() {
  useSEO({
    title: "Knowledge Center · Healthcare Operations & Compliance Articles",
    description:
      "Field notes on Medicaid pending workflow, survey readiness, documentation, social services compliance, and nursing home operations, from Sierra Thomas, AthenaCrest Defense.",
    path: "/knowledge-center",
  });
  const [featured, ...rest] = BLOG_POSTS;

  return (
    <div data-testid="blog-page" className="bg-ivory">
      <section className="section-pad pt-32 bg-paper">
        <div className="container-luxe max-w-5xl">
          <div className="overline mb-5">Knowledge Center</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-navy leading-[0.95] mb-6">
            Field notes from<br />
            <span className="italic text-teal">inside the building.</span>
          </h1>
          <p className="font-sans text-lg text-navy/75 max-w-3xl leading-relaxed">
            Practical operational, compliance, and revenue strategy for skilled
            nursing facilities, long-term care organizations, and the attorneys
            who defend them. Updated regularly.
          </p>
        </div>
      </section>

      {/* FEATURED */}
      <section className="section-pad" data-testid="featured-post">
        <div className="container-luxe">
          <Link
            to={`/knowledge-center/${featured.slug}`}
            className="group block border border-champagne-200 hover:border-gold transition-colors p-10 lg:p-16"
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-2">
                <div className="overline">Featured</div>
              </div>
              <div className="lg:col-span-8">
                <div className="font-sans text-xs uppercase tracking-overline text-gold mb-3">
                  {featured.category}
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6 group-hover:text-teal transition-colors">
                  {featured.title}
                </h2>
                <p className="font-sans text-base sm:text-lg text-navy/70 leading-relaxed mb-6">
                  {featured.excerpt}
                </p>
                <div className="flex items-center gap-4 text-xs font-sans text-navy/50">
                  <span>{featured.date}</span>
                  <span className="w-px h-3 bg-navy/30" />
                  <span>{featured.readTime}</span>
                </div>
              </div>
              <div className="lg:col-span-2 flex lg:justify-end">
                <ArrowUpRight className="h-8 w-8 text-navy/40 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* GRID */}
      <section className="pb-24" data-testid="post-grid">
        <div className="container-luxe">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/knowledge-center/${p.slug}`}
                className="luxe-card group flex flex-col"
                data-testid={`blog-card-${p.slug}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="overline text-[10px]">{p.category}</div>
                  <ArrowUpRight className="h-4 w-4 text-navy/40 group-hover:text-gold transition-colors" />
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl text-navy mb-4 group-hover:text-teal transition-colors leading-tight">
                  {p.title}
                </h3>
                <p className="font-sans text-sm text-navy/65 leading-relaxed mb-6 flex-1">
                  {p.excerpt}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-champagne-200 text-xs font-sans text-navy/50">
                  <span>{p.date}</span>
                  <span>{p.readTime}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        testId="blog-cta-band"
        eyebrow="Beyond the Article"
        headline="Bring the playbook into your facility."
        sub="Every article reflects a real engagement framework. Book a confidential consultation to apply it inside your building."
      />
    </div>
  );
}
